import { HowToVote, HowToVoteOutlined, MoreVertRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { Links } from "../../constants";
import { useSession } from "../../hooks";
import { IssueDto, ProposalDto, useRemoveProposalUpvote, useUpvoteProposal } from "../../http";
import { TextViewer } from "src/components";

interface IProps {
  issue: IssueDto;
  proposal: ProposalDto;
  onEditClicked?: (proposal: ProposalDto) => void;
  onDeleteClicked?: (proposal: ProposalDto) => void;
}

const Proposal = (props: IProps) => {
  const user = useSession();
  const { mutate: upvoteProposal } = useUpvoteProposal();
  const { mutate: removeProposalUpvote } = useRemoveProposalUpvote();
  const textViewerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const shouldShowEditControls =
    props.proposal.createdByUser.userId === user.userId && Boolean(props.onEditClicked || props.onDeleteClicked);

  const handleNoteContentUpdated = useCallback(() => {
    if (!props.proposal.note || !textViewerRef.current) {
      setShowMoreButton(false);
      return;
    }

    setShowMoreButton(textViewerRef.current.scrollHeight > textViewerRef.current.clientHeight + 1);
  }, [props.proposal.note]);

  async function handleUpvoteClicked(proposal: ProposalDto) {
    if (proposal.upvotes.some((x) => x.userId === user.userId)) {
      removeProposalUpvote({
        proposalGuid: proposal.guid,
        issueGuid: props.issue.guid,
        userId: user.userId,
      });
    } else {
      upvoteProposal({
        proposalGuid: proposal.guid,
        issueGuid: props.issue.guid,
        user: { userId: user.userId, displayName: user.displayName, enabled: true, profilePhotoSmallUrl: undefined },
      });
    }
  }

  function handleEditClicked() {
    setMenuAnchor(null);
    setIsExpanded(false);
    props.onEditClicked?.(props.proposal);
  }

  function handleDeleteClicked() {
    setMenuAnchor(null);
    props.onDeleteClicked?.(props.proposal);
  }

  return (
    <Card
      sx={{
        zIndex: 2,
        border: `solid 1px ${theme.palette.grey[200]}`,
        p: 0.5,
      }}
      elevation={0}
    >
      <Stack>
        <Stack
          key={props.proposal.guid}
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: "start",
            width: "100%"
          }}>
          <Stack sx={{
            width: "100%"
          }}>
            <Typography variant="body2">{props.proposal.description}</Typography>
            <Typography variant="caption">{`proposed by ${props.proposal.createdByUser.displayName}`}</Typography>
          </Stack>

          <Tooltip
            placement="bottom-end"
            title={
              props.proposal.upvotes.length > 0
                ? props.proposal.upvotes
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((u, i) => [
                      <Typography key={i} sx={{ display: "inline-block", fontSize: 13, marginRight: "2px" }}>
                        <Link href={Links.User(u.userId)}>{u.displayName}</Link>
                        {i < props.proposal.upvotes.length - 1 && ", "}
                      </Typography>,
                    ])
                : null
            }
          >
            <Button
              onClick={(e) => {
                handleUpvoteClicked(props.proposal);
                e.stopPropagation();
              }}
              variant="tertiary"
              sx={{ minWidth: "120px" }}
              startIcon={
                props.proposal.upvotes.findIndex((x) => x.userId === user.userId) !== -1 ? (
                  <HowToVote />
                ) : (
                  <HowToVoteOutlined />
                )
              }
            >
              {`${props.proposal.upvotes.findIndex((x) => x.userId === user.userId) !== -1 ? "Voted" : "Vote"} (${
                props.proposal.upvotes.length
              })`}
            </Button>
          </Tooltip>

          <IconButton
            size="small"
            onClick={(e) => {
              setMenuAnchor(e.currentTarget);
            }}
            disabled={!shouldShowEditControls}
            sx={{ visibility: shouldShowEditControls ? "visible" : "hidden" }}
          >
            <MoreVertRounded />
          </IconButton>
          {shouldShowEditControls && (
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={handleEditClicked} disabled={!props.onEditClicked}>
                Edit
              </MenuItem>
              <MenuItem onClick={handleDeleteClicked} sx={{ color: "error.main" }} disabled={!props.onDeleteClicked}>
                Delete
              </MenuItem>
            </Menu>
          )}
        </Stack>
        {props.proposal.note && (
          <Stack spacing={0.5}>
            <Box
              ref={textViewerRef}
              sx={
                isExpanded
                  ? undefined
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }
              }
            >
              <TextViewer
                value={props.proposal.note}
                sx={{ width: "100%" }}
                onContentUpdated={handleNoteContentUpdated}
              />
            </Box>
            {showMoreButton && (
              <Button
                variant="tertiary"
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ alignSelf: "flex-start" }}
              >
                {isExpanded ? "Show less" : "Show more"}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export { Proposal };
